import { useCMSPage } from '@web-app/hooks/use-cms-page';
import type { InferGetServerSidePropsType } from 'next/types';

import { getPageParamsQueriesKeys, getParamsQueriesKeys } from '@core/api';
import { ARTICLES_TO_SHOW } from '@core/constant';
import { formatMetas, getSanityQuery } from '@core/sanity/utils';
import { ERouting } from '@core/type';
import { DynamicSEOEntity, HomepageArticlesResponse } from '@core/type/api';
import { SanityBlogHome } from '@core/type/sanity';
import { getLanguageFromTags } from '@core/util';

import { fetchSanityData } from '../../helpers/make-server-props/helpers/sanity';
import { isRedirect, makeServerSideProps } from '../../helpers/make-server-props/make-server-props';
import { ViewBlogList } from '../../view';

export type BlogHomeQuery = {
  page: string;
};

export type PageBlogHomeProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const PageBlogHome = (pageProps: PageBlogHomeProps) =>
  useCMSPage({
    ...pageProps,
    ViewPage: ViewBlogList,
  });

const getServerSideProps = makeServerSideProps<
  Required<Omit<DynamicSEOEntity, 'breadcrumbs' | 'alternates'>> & {
    cmsResult: SanityBlogHome;
    pageId: string;
  },
  Record<string, string>,
  BlogHomeQuery,
  ERouting.BLOG_HOME
>(ERouting.BLOG_HOME, async (ctx) => {
  const res = await fetchSanityData<SanityBlogHome>(ctx);
  if (isRedirect(res)) {
    return res;
  }
  const cmsResult = res.props;

  const queryKey = getPageParamsQueriesKeys(ctx.keyRoute);
  const prefetch = getParamsQueriesKeys({ ...ctx, queryKey });
  await ctx.prefetch([{ queryKey: prefetch.queries, queryFn: prefetch.queryFn }]);
  const data = ctx.queryClient.getQueryData<HomepageArticlesResponse>(prefetch.queries);
  ctx.prefetches.push(
    getSanityQuery('blogHomeArticlesQuery', {
      language: getLanguageFromTags(ctx.language),
      page: ctx.props.query.page,
      perPage: data?.leadMagnet ? ARTICLES_TO_SHOW - 1 : ARTICLES_TO_SHOW,
      mainArticleId: cmsResult.mainArticle?._id ?? '',
    }),
  );

  return {
    props: {
      cmsResult: cmsResult,
      pageId: cmsResult._id,

      metas: formatMetas(cmsResult.metadata),
      blogCategories: cmsResult.blogCategories,
    },
  };
});

export { getServerSideProps };

export default PageBlogHome;
